import React from 'react';
import { Box, Typography } from '@mui/material';
import { Badge as BadgeIcon } from '@mui/icons-material';
import { appColors } from '../../Config/Theme';
import { IncidentRosterPerson } from '../../Types/Incident';
import { getIncidentDayBadgeText } from '../../Utils/personHelpers';

interface IncidentDayBadgeInfoProps {
  /** Pass the full person to use the helper, or pass individual fields. */
  person?: IncidentRosterPerson;
  lastBadgeAtUtc?: string | null;
  lastBadgeLocation?: string | null;
}

const IncidentDayBadgeInfo: React.FC<IncidentDayBadgeInfoProps> = ({ person, lastBadgeAtUtc, lastBadgeLocation }) => {
  // If a full person is passed, use the centralized helper
  if (person) {
    const text = getIncidentDayBadgeText(person);
    if (!text) return null;

    const isNoActivity = text.includes('No badge activity');
    const isManualAdd = text === 'Manual Add';
    const isAssignedOffice = text.includes('Assigned Office');
    const isBadgeUnavailable = text.includes('Badge details unavailable');
    const isMuted = isNoActivity || isManualAdd || isAssignedOffice || isBadgeUnavailable;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <BadgeIcon sx={{ fontSize: 15, color: isMuted ? appColors.text.placeholder : appColors.primary.accent }} />
        <Typography variant="caption" sx={{ color: isMuted ? appColors.text.placeholder : appColors.text.heading, fontSize: '0.72rem', fontWeight: isMuted ? 400 : 600, fontStyle: isMuted ? 'italic' : 'normal' }}>
          {text}
        </Typography>
      </Box>
    );
  }

  // Legacy: individual props
  if (!lastBadgeAtUtc) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <BadgeIcon sx={{ fontSize: 15, color: appColors.text.placeholder }} />
        <Typography variant="caption" sx={{ color: appColors.text.placeholder, fontSize: '0.72rem', fontStyle: 'italic' }}>
          No badge activity today
        </Typography>
      </Box>
    );
  }

  const timeStr = formatBadgeTime(lastBadgeAtUtc);
  const dateStr = formatBadgeDate(lastBadgeAtUtc);
  const parts = [`Last badge ${dateStr}: ${timeStr}`, lastBadgeLocation].filter(Boolean);

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
      <BadgeIcon sx={{ fontSize: 15, color: appColors.primary.accent }} />
      <Typography variant="caption" sx={{ color: appColors.text.heading, fontSize: '0.72rem', fontWeight: 600 }}>
        {parts.join(' \u00B7 ')}
      </Typography>
    </Box>
  );
};

/** Format a local datetime string to a short time string. */
const formatBadgeTime = (localDatetime: string): string => {
  try {
    const d = new Date(localDatetime);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return '';
  }
};

/** Format a local datetime string to a readable date string (e.g. "May 14, 2026"). */
const formatBadgeDate = (localDatetime: string): string => {
  try {
    const d = new Date(localDatetime);
    return d.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  } catch {
    return '';
  }
};

export default React.memo(IncidentDayBadgeInfo);
